import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { epicsApi, ticketsApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import { TICKET_TYPES, TYPE_LABELS, type Epic, type TicketType } from "../api/types";
import { useTeams } from "../hooks/useTeams";
import { EmptyState, ErrorBanner, Loading } from "../components/Feedback";
import { Icon } from "../components/Icon";

export function TicketCreatePage() {
  const { teams, error: teamsError, selectedId, select } = useTeams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const initialTeam = params.get("teamId");
  const [teamId, setTeamId] = useState<string | null>(initialTeam ?? selectedId);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [type, setType] = useState<TicketType>("bug");
  const [epicId, setEpicId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!teamId && selectedId) setTeamId(selectedId);
  }, [selectedId, teamId]);

  useEffect(() => {
    if (!teamId) return;
    setEpicId("");
    epicsApi.listByTeam(teamId).then(setEpics).catch(() => setEpics([]));
  }, [teamId]);

  const teamName = useMemo(() => teams?.find((t) => t.id === teamId)?.name ?? "", [teams, teamId]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!teamId) return;
    setError(null);
    setBusy(true);
    try {
      const ticket = await ticketsApi.create({
        teamId,
        type,
        epicId: epicId || null,
        title: title.trim(),
        body,
      });
      navigate(`/tickets/${ticket.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to create ticket.");
    } finally {
      setBusy(false);
    }
  };

  if (teams === null) return <Loading />;
  if (teamsError) return <ErrorBanner message={teamsError} />;
  if (teams.length === 0) return <EmptyState message="Create a team first, then add tickets." />;

  return (
    <section className="page page-narrow">
      <div>
        <button className="link-button" onClick={() => navigate("/board")}>
          <Icon name="arrowLeft" size={16} /> Back to board
        </button>
        <h1 className="page-title" style={{ marginTop: 8 }}>
          New ticket
        </h1>
      </div>

      {error && <ErrorBanner message={error} />}

      <form className="card card-pad detail-form" onSubmit={onSubmit}>
        <div className="detail-form-row">
          <label className="field">
            <span className="field-label">Team</span>
            <select
              value={teamId ?? ""}
              onChange={(e) => {
                setTeamId(e.target.value);
                select(e.target.value);
              }}
            >
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as TicketType)}>
              {TICKET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Epic (optional)</span>
            <select value={epicId} onChange={(e) => setEpicId(e.target.value)}>
              <option value="">— None —</option>
              {epics.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="field">
          <span className="field-label">Title</span>
          <input value={title} required placeholder="Short summary" onChange={(e) => setTitle(e.target.value)} />
        </label>

        <label className="field">
          <span className="field-label">Body</span>
          <textarea
            rows={7}
            value={body}
            placeholder="Describe the work. Markdown is fine."
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="secondary" onClick={() => navigate("/board")}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || !title.trim()}>
            {busy ? "Creating…" : `Create in ${teamName}`}
          </button>
        </div>
      </form>
    </section>
  );
}
